import { create } from 'zustand';
import type { DesktopAnswer } from './welcomeTourModel';

/** Where someone is in the tour: kept while they read a help article, so they come back to the same step. */
export interface TourProgress {
  answer: DesktopAnswer;
  index: number;
  /** The furthest step reached; those steps can be opened from the step list. */
  visited: number;
  /** The steps that have been on screen (checked in the step list). */
  seen: number[];
  /** The selected item on each selectable list, by step. */
  selected: Record<string, number>;
  startChoice: number | null;
  facilityChoice: number | null;
}

/**
 * Whether the welcome tour is open, or paused while its "Read:" link has the help article open in
 * this tab. WelcomeTourHost (in MainLayout) opens it the first time someone signs in; Help > Help
 * Articles and My Settings > Notifications open it again with `show`.
 *
 * Kept in memory: the article opens in the same tab, signed in, and "Back to the tour" brings the
 * tour back at the same page. A new tab would start with empty session storage and have to sign in
 * and pick the organization again.
 */
interface WelcomeTourState {
  open: boolean;
  /** Set while an article from the tour is open; the bar to go back to the tour shows. */
  paused: TourProgress | null;
  /** What the tour opens at when it comes back from an article. */
  resumeFrom: TourProgress | null;
  show: () => void;
  pause: (progress: TourProgress) => void;
  resume: () => void;
  hide: () => void;
}

export const useWelcomeTourStore = create<WelcomeTourState>((set, get) => ({
  open: false,
  paused: null,
  resumeFrom: null,
  show: () => set({ open: true, paused: null, resumeFrom: null }),
  pause: (progress) => set({ open: false, paused: progress, resumeFrom: null }),
  resume: () => set({ open: true, resumeFrom: get().paused, paused: null }),
  hide: () => set({ open: false, paused: null, resumeFrom: null }),
}));
